import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SessionPermissionService } from '../auth/session-permission.service';
import { PERMISSION_KEYS } from '../auth/permission-keys';
import { Module } from 'src/assets/types/interface/module.interface';
import { Program } from 'src/assets/types/interface/program.interface';
import { environment } from 'src/environments/environment';

const TEMPORARY_ENVIRONMENTS_EQUIPMENTS_PROGRAM: Program = {
  uuid: 'temporary-environments-equipments-crud',
  name: 'Ambientes e Ativos',
  description: 'Validação temporária do CRUD de ambientes e ativos.',
  route: '/configuracoes/ambientes-ativos',
  menu_position: 99,
  icon: 'building-warehouse',
  show_in_menu: true,
};

const SERVICE_AGENDA_PROGRAM: Program = {
  uuid: 'service-agenda',
  name: 'Agenda de Serviços',
  description: 'Agenda de visitas técnicas e execuções.',
  route: '/gestao/agenda',
  menu_position: 2.1,
  icon: 'calendar-event',
  show_in_menu: true,
};

const TEMPORARY_MANAGEMENT_DASHBOARD_PROGRAM: Program = {
  uuid: 'temporary-management-dashboard',
  name: 'Dashboard',
  description: 'Indicadores gerenciais da operação.',
  route: '/gestao/dashboard',
  menu_position: 0,
  icon: 'layout-dashboard',
  show_in_menu: true,
};

interface StoredTenant {
  type?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly apiUrl = environment.AUTH_API_URL;
  constructor(
    private http: HttpClient,
    private sessionPermissionService: SessionPermissionService,
  ) {}

  getMenu(): Observable<Module[]> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http
      .get<Module[]>(`${this.apiUrl}/module`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      })
      .pipe(map((modules) => this.withTemporaryPrograms(modules)));
  }

  private withTemporaryPrograms(modules: Module[]): Module[] {
    return this.withServiceAgendaProgram(
      this.withTemporaryManagementDashboardProgram(
        this.withTemporaryEnvironmentsEquipmentsProgram(modules),
      ),
    );
  }

  private withServiceAgendaProgram(modules: Module[]): Module[] {
    const tenant = this.sessionPermissionService.getTypeTenant();
    const profile = this.sessionPermissionService.getProfileName() ?? '';
    const profiles =
      tenant === 'Tomador'
        ? ['Site Admin', 'Admin', 'Gestor', 'Aprovador', 'Solicitante']
        : tenant === 'Prestador'
          ? ['Site Admin', 'Admin', 'Gestor']
          : [];
    const canList = [
      PERMISSION_KEYS.technicalVisit.list,
      PERMISSION_KEYS.serviceOrder.list,
    ].some((permission) =>
      this.sessionPermissionService.hasPermission(permission),
    );
    if (!canList || !profiles.includes(profile)) return modules;

    return modules.map((module) => {
      if (module.route !== '/gestao') return module;
      const programs = module.programs ?? [];
      if (
        programs.some(
          (program) => program.route === SERVICE_AGENDA_PROGRAM.route,
        )
      )
        return module;
      const hasVisits = programs.some(
        (program) => program.route === '/gestao/visitas',
      );
      return {
        ...module,
        programs: hasVisits
          ? programs.map((program) =>
              program.route === '/gestao/visitas'
                ? {
                    ...program,
                    name: SERVICE_AGENDA_PROGRAM.name,
                    description: SERVICE_AGENDA_PROGRAM.description,
                    route: SERVICE_AGENDA_PROGRAM.route,
                    icon: SERVICE_AGENDA_PROGRAM.icon,
                  }
                : program,
            )
          : [...programs, { ...SERVICE_AGENDA_PROGRAM }],
      };
    });
  }

  private withTemporaryManagementDashboardProgram(modules: Module[]): Module[] {
    if (
      this.getTenantType() !== 'Tomador' ||
      !this.sessionPermissionService.hasPermission(
        PERMISSION_KEYS.serviceOrder.program,
      )
    ) {
      return modules;
    }

    return modules.map((module) => {
      if (module.route !== '/gestao') return module;
      const programs = module.programs ?? [];
      if (
        programs.some(
          (program) =>
            program.route === TEMPORARY_MANAGEMENT_DASHBOARD_PROGRAM.route,
        )
      )
        return module;

      return {
        ...module,
        programs: [{ ...TEMPORARY_MANAGEMENT_DASHBOARD_PROGRAM }, ...programs],
      };
    });
  }

  private getTenantType(): string | null {
    return (
      this.sessionPermissionService.getTypeTenant() ??
      this.parseLocalStorageItem<StoredTenant>('tenant')?.type ??
      null
    );
  }

  private parseLocalStorageItem<T>(key: string): T | null {
    try {
      return JSON.parse(localStorage.getItem(key) ?? '{}') as T;
    } catch {
      return null;
    }
  }

  private withTemporaryEnvironmentsEquipmentsProgram(
    modules: Module[],
  ): Module[] {
    if (this.sessionPermissionService.getTypeTenant() !== 'Tomador') {
      return modules;
    }

    const settingsModule = modules.find(
      (module) => module.route === '/configuracoes',
    );

    if (!settingsModule) {
      return modules;
    }

    const programs = settingsModule.programs ?? [];
    const hasEnvironmentsEquipmentsProgram = programs.some(
      (program) =>
        program.route === TEMPORARY_ENVIRONMENTS_EQUIPMENTS_PROGRAM.route,
    );

    if (hasEnvironmentsEquipmentsProgram) {
      return modules;
    }

    settingsModule.programs = [
      ...programs,
      // TEMPORARY_ENVIRONMENTS_EQUIPMENTS_PROGRAM,
    ];

    return modules;
  }
}
